import {
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  PrimaryBlueButton,
  SecondaryBlueButton,
  Severity
} from "@velaro/velaro-component-lib";
import useApi from "@/hooks/useApi";
import React, { useEffect, useState } from "react";
import { RadioDot } from "@/components/RadioOption";
import useToastAlert from "@/hooks/useToastAlert";
import { IVRVoiceConfiguration } from "../types";
import useTeams from "@/hooks/useTeams";

interface IvrCapabilities {
  enableIvrBridgeCall: boolean;
  enableIvrTriage: boolean;
  enableIvrAvailabilityCheck: boolean;
  enableIvrSmartQueue: boolean;
  enableIvrAcd: boolean;
  enableMultilingualIVR: boolean; // true when multilingualLanguageLimit > 0
  multilingualLanguageLimit: number; // 0=English only, 3=standard, 5=premium
  isVelaroAdmin: boolean;
}

const DEFAULT_TRIAGE_MESSAGE =
  "I wasn't able to resolve your question. Press 1 to request a callback, press 2 for our hours and location, or press 0 to return to the main menu.";

const TRANSFER_MODE_OPTIONS = [
  {
    value: "always",
    label: "Always transfer",
    description: "Immediately connect the caller to an agent whenever they ask, regardless of availability.",
    gate: null
  },
  {
    value: "triage",
    label: "Triage menu first",
    description: "Play a DTMF fallback menu (callback / hours / main menu) before bridging to a live agent.",
    gate: "enableIvrTriage"
  },
  {
    value: "availability",
    label: "Check availability first",
    description: "Check agent queue depth before bridging. If no agents are available, offer a callback instead of ringing an empty queue.",
    gate: "enableIvrAvailabilityCheck"
  },
  {
    value: "queue",
    label: "Virtual queue",
    description: "If agents are logged in but busy, hold the caller in a virtual queue and auto-bridge when an agent is free.",
    gate: "enableIvrSmartQueue"
  },
  {
    value: "acd",
    label: "ACD routing",
    description: "Route to the team with the shortest current queue depth (ACD / skills-based routing).",
    gate: "enableIvrAcd"
  }
];

interface Props {
  show: boolean;
  onClose(): void;
  onSave(configuration: IVRVoiceConfiguration): void;
  existingConfiguration?: IVRVoiceConfiguration;
}

const LANGUAGE_OPTIONS = [
  { value: "auto", label: "Auto-detect — identifies the caller's language automatically (recommended)" },
  { value: "en", label: "English" },
  { value: "es", label: "Spanish" },
  { value: "fr", label: "French" },
  { value: "de", label: "German" },
  { value: "pt", label: "Portuguese" },
  { value: "it", label: "Italian" },
  { value: "nl", label: "Dutch" },
  { value: "pl", label: "Polish" },
  { value: "ja", label: "Japanese" },
  { value: "vi", label: "Vietnamese" },
  { value: "zh", label: "Chinese (Mandarin)" },
  { value: "ko", label: "Korean" },
  { value: "ar", label: "Arabic" },
  { value: "hi", label: "Hindi" }
];

// Speaking speed: customer-friendly labels map to ElevenLabs speed values
const SPEED_OPTIONS = [
  { value: 0.8, label: "Slower — easier to follow" },
  { value: 1.0, label: "Normal — recommended" },
  { value: 1.15, label: "Slightly faster" },
  { value: 1.3, label: "Faster — concise responses" }
];

const emptyForm = (): Partial<IVRVoiceConfiguration> => ({
  name: "",
  voiceProvider: "ElevenLabs",
  voiceId: "",
  model: "multilingual",
  speed: 1.0,
  stability: 0.5,
  similarity: 0.75,
  transcriptionProvider: "Deepgram",
  transcriptionLanguage: "auto",
  speechModel: "nova-3-general",
  vocabularyHints: "",
  interruptSensitivity: "medium",
  interruptible: "any",
  reportInputDuringAgentSpeech: false,
  dtmfDetection: true,
  teamId: undefined,
  maxCallDurationSeconds: undefined,
  callDurationWarningSeconds: undefined,
  callDurationExceededMessage: undefined,
  autoNoiseDetection: false,
  minConfidenceThreshold: 0.5,
  minWordCount: 1,
  noiseAdvisoryThreshold: 3,
  noiseAdvisoryMessage: undefined,
  transferRouting: [],
  escalationBridgeEnabled: true,
  escalationCallbackEnabled: true,
  escalationSmsEnabled: true,
  escalationEmailEnabled: true,
  transferMethod: "bridge",
  sipTransferUri: "",
  transferMode: "always",
  triageFallbackMessage: undefined,
  alwaysTransferNumber: undefined,
  idleCheckInSeconds: undefined,
  transferNoAnswerMessage: undefined,
  recordingDisclosureEnabled: true,
  recordingDisclosureText: undefined,
  welcomeGreeting: undefined
});

export default function IVRVoiceConfigurationModal(props: Props) {
  const api = useApi();
  const { teams } = useTeams();
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const toastAlert = useToastAlert();
  const [capabilities, setCapabilities] = useState<IvrCapabilities>({
    enableIvrBridgeCall: false,
    enableIvrTriage: false,
    enableIvrAvailabilityCheck: false,
    enableIvrSmartQueue: false,
    enableIvrAcd: false,
    enableMultilingualIVR: false,
    multilingualLanguageLimit: 0,
    isVelaroAdmin: false
  });

  const [formData, setFormData] = useState<Partial<IVRVoiceConfiguration>>(emptyForm());

  useEffect(() => {
    setFormData(props.existingConfiguration ? { ...props.existingConfiguration } : emptyForm());
  }, [props.existingConfiguration, props.show]);

  useEffect(() => {
    async function fetchCapabilities() {
      try {
        const r = await api.admin.get("IVRVoiceConfigurations/Capabilities");
        if (r.ok) {
          const d = await r.json();
          setCapabilities({
            enableIvrBridgeCall: d.enableIvrBridgeCall ?? false,
            enableIvrTriage: d.enableIvrTriage ?? false,
            enableIvrAvailabilityCheck: d.enableIvrAvailabilityCheck ?? false,
            enableIvrSmartQueue: d.enableIvrSmartQueue ?? false,
            enableIvrAcd: d.enableIvrAcd ?? false,
            multilingualLanguageLimit: d.multilingualLanguageLimit ?? 0,
            enableMultilingualIVR: (d.multilingualLanguageLimit ?? 0) > 0 || (d.enableMultilingualIVR ?? false),
            isVelaroAdmin: d.isVelaroAdmin ?? false
          });
        }
      } catch {
        // keep defaults
      }
    }
    if (props.show) fetchCapabilities();
  }, [props.show, api]);

  async function handleSubmit() {
    if (!formData.name?.trim()) {
      setError("Configuration name is required.");
      return;
    }
    if (!formData.voiceId?.trim()) {
      setError("Voice ID is required.");
      return;
    }
    if (formData.callDurationWarningSeconds && !formData.maxCallDurationSeconds) {
      setError("Max call duration is required when a warning time is set.");
      return;
    }
    if (
      formData.maxCallDurationSeconds &&
      formData.callDurationWarningSeconds &&
      formData.callDurationWarningSeconds >= formData.maxCallDurationSeconds
    ) {
      setError("Warning time must be less than max call duration.");
      return;
    }

    setSending(true);
    setError("");

    try {
      const payload = {
        name: formData.name,
        voiceProvider: formData.voiceProvider ?? "ElevenLabs",
        voiceId: formData.voiceId,
        model: formData.model ?? "multilingual",
        speed: formData.speed ?? 1.0,
        stability: formData.stability ?? 0.5,
        similarity: formData.similarity ?? 0.75,
        transcriptionProvider: formData.transcriptionProvider ?? "Deepgram",
        transcriptionLanguage: formData.transcriptionLanguage ?? "en",
        speechModel: formData.speechModel ?? "nova-3-general",
        vocabularyHints: formData.vocabularyHints ?? "",
        interruptSensitivity: formData.interruptSensitivity ?? "medium",
        interruptible: formData.interruptible ?? "any",
        reportInputDuringAgentSpeech: formData.reportInputDuringAgentSpeech ?? false,
        dtmfDetection: formData.dtmfDetection ?? true,
        teamId: formData.teamId ?? null,
        maxCallDurationSeconds: formData.maxCallDurationSeconds ?? null,
        callDurationWarningSeconds: formData.callDurationWarningSeconds ?? null,
        callDurationExceededMessage: formData.callDurationExceededMessage ?? null,
        autoNoiseDetection: formData.autoNoiseDetection ?? false,
        minConfidenceThreshold: formData.minConfidenceThreshold ?? 0.5,
        minWordCount: formData.minWordCount ?? 1,
        noiseAdvisoryThreshold: formData.noiseAdvisoryThreshold ?? 3,
        noiseAdvisoryMessage: formData.noiseAdvisoryMessage ?? null,
        transferRouting: formData.transferRouting ?? [],
        escalationBridgeEnabled: formData.escalationBridgeEnabled ?? true,
        escalationCallbackEnabled: formData.escalationCallbackEnabled ?? true,
        escalationSmsEnabled: formData.escalationSmsEnabled ?? true,
        escalationEmailEnabled: formData.escalationEmailEnabled ?? true,
        transferMethod: formData.transferMethod ?? "bridge",
        sipTransferUri: formData.sipTransferUri ?? null,
        transferMode: formData.transferMode ?? "always",
        triageFallbackMessage: formData.triageFallbackMessage ?? null,
        alwaysTransferNumber: formData.alwaysTransferNumber ?? null,
        idleCheckInSeconds: formData.idleCheckInSeconds ?? null,
        transferNoAnswerMessage: formData.transferNoAnswerMessage ?? null,
        recordingDisclosureEnabled: formData.recordingDisclosureEnabled ?? false,
        recordingDisclosureText: formData.recordingDisclosureText ?? null,
        welcomeGreeting: formData.welcomeGreeting ?? null
      };

      const response = props.existingConfiguration
        ? await api.messaging.put(`IVRVoiceConfigurations/${props.existingConfiguration.id}`, payload)
        : await api.messaging.post("IVRVoiceConfigurations", payload);

      if (response.ok) {
        const saved = await response.json();
        props.onSave(saved);
        toastAlert.displayToast(
          Severity.Success,
          props.existingConfiguration
            ? "Voice configuration updated."
            : "Voice configuration created."
        );
        props.onClose();
      } else {
        const err = await response.json().catch(() => ({}));
        setError(err.title ?? "Failed to save voice configuration.");
      }
    } catch {
      setError("An error occurred while saving.");
    } finally {
      setSending(false);
    }
  }

  const maxDurationMinutes = formData.maxCallDurationSeconds
    ? Math.round(formData.maxCallDurationSeconds / 60)
    : null;

  return (
    <Modal show={props.show} maxWidth="max-w-2xl">
      <ModalHeader title="IVR Voice Configuration" onClose={props.onClose} />
      <ModalBody>
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-300 text-red-700 rounded-lg text-sm">
            {error}
          </div>
        )}

        <div className="space-y-5">

          {/* Name + Team */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Configuration Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                className="input w-full text-sm"
                placeholder="e.g., Support Team Voice"
                value={formData.name ?? ""}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                maxLength={256}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Team
              </label>
              <select
                className="input w-full text-sm"
                value={formData.teamId ?? ""}
                onChange={(e) =>
                  setFormData({ ...formData, teamId: e.target.value ? Number(e.target.value) : undefined })
                }
              >
                <option value="">All Teams</option>
                {teams.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Welcome Greeting */}
          <div className="border-t border-slate-100 pt-4">
            <div className="text-sm font-semibold text-slate-700 mb-1">IVR Opening Line <span className="text-xs font-normal text-slate-400">(phone calls only)</span></div>
            <p className="text-xs text-slate-500 mb-3">
              Spoken the instant the caller connects — before the bot loads. Fills the silence at call start.
              Does not appear in web chat, SMS, or any other channel. Keep it short (under 10 words).
            </p>
            <textarea
              className="input w-full text-sm"
              rows={2}
              placeholder="e.g., Thank you for calling Sullivan Tire."
              value={formData.welcomeGreeting ?? ""}
              onChange={(e) => setFormData({ ...formData, welcomeGreeting: e.target.value || undefined })}
              maxLength={500}
            />
          </div>

          {/* Voice */}
          <div className="border-t border-slate-100 pt-4">
            <div className="text-sm font-semibold text-slate-700 mb-3">Voice</div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Voice ID <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  className="input w-full text-sm font-mono"
                  placeholder="e.g., EXAVITQu4mRLihs9xqJ9"
                  value={formData.voiceId ?? ""}
                  onChange={(e) => setFormData({ ...formData, voiceId: e.target.value })}
                  maxLength={256}
                />
                <p className="text-xs text-slate-400 mt-1">
                  From your ElevenLabs voice library.
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Speaking Speed
                </label>
                <select
                  className="input w-full text-sm"
                  value={formData.speed ?? 1.0}
                  onChange={(e) => setFormData({ ...formData, speed: Number(e.target.value) })}
                >
                  {SPEED_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Language */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-slate-700">
                Supported Languages
              </label>
              {!capabilities.enableMultilingualIVR ? (
                <span className="text-xs font-semibold text-amber-600 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full">🔒 Multilingual add-on required</span>
              ) : (
                <span className="text-xs text-slate-500">
                  {(() => {
                    const selected = (formData.supportedLanguages as string[] | undefined) ?? ["en"];
                    const nonEn = selected.filter(l => l !== "en").length;
                    const limit = capabilities.multilingualLanguageLimit;
                    return `${nonEn} of ${limit} additional language${limit !== 1 ? "s" : ""} selected`;
                  })()}
                </span>
              )}
            </div>

            {capabilities.enableMultilingualIVR ? (
              <div className="border border-slate-200 rounded-lg divide-y divide-slate-100 bg-white">
                {LANGUAGE_OPTIONS.filter(l => l.value !== "auto").map((lang) => {
                  const selected = (formData.supportedLanguages as string[] | undefined) ?? ["en"];
                  const isChecked = selected.includes(lang.value);
                  const isEnglish = lang.value === "en";
                  const nonEnSelected = selected.filter(l => l !== "en").length;
                  const atLimit = nonEnSelected >= capabilities.multilingualLanguageLimit;
                  const disabled = isEnglish || (!isChecked && atLimit);
                  return (
                    <label key={lang.value} className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-slate-50 ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}>
                      <input
                        type="checkbox"
                        className="accent-blue-600"
                        checked={isChecked}
                        disabled={disabled}
                        onChange={(e) => {
                          const current = (formData.supportedLanguages as string[] | undefined) ?? ["en"];
                          const updated = e.target.checked
                            ? [...current, lang.value]
                            : current.filter(l => l !== lang.value);
                          setFormData({ ...formData, supportedLanguages: updated, transcriptionLanguage: updated.length > 1 ? "auto" : "en" });
                        }}
                      />
                      <span className="text-sm text-slate-700">{lang.label}</span>
                      {isEnglish && <span className="text-xs text-slate-400 ml-auto">Always included</span>}
                    </label>
                  );
                })}
              </div>
            ) : (
              <div className="border border-amber-100 rounded-lg bg-amber-50 p-3">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm font-medium text-slate-700">English</span>
                  <span className="text-xs text-slate-400">Only language available on current plan</span>
                </div>
                <p className="text-xs text-amber-700 leading-relaxed">
                  Unlock Spanish, Vietnamese, Mandarin, Korean, and 10+ more languages with the Multilingual IVR add-on.
                  Contact your account manager to upgrade.
                </p>
              </div>
            )}

            {capabilities.enableMultilingualIVR && (
              <p className="text-xs text-slate-400 mt-2">
                When multiple languages are selected, callers are auto-detected and the AI responds in their language automatically. English is always included.
              </p>
            )}
          </div>

          {/* Interruption */}
          <div className="border-t border-slate-100 pt-4">
            <div className="text-sm font-semibold text-slate-700 mb-1">Caller Interruption</div>
            <p className="text-xs text-slate-500 mb-3">
              Controls whether callers can cut off the bot mid-response by speaking or pressing a key.
            </p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Allow interruptions
                </label>
                <select
                  className="input w-full text-sm"
                  value={formData.interruptible ?? "any"}
                  onChange={(e) => setFormData({ ...formData, interruptible: e.target.value })}
                >
                  <option value="any">Yes — speech or keypad (recommended)</option>
                  <option value="dtmf">Keypad only</option>
                  <option value="speech">Voice only</option>
                  <option value="none">No — bot always finishes speaking</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Interruption sensitivity
                </label>
                <select
                  className="input w-full text-sm"
                  value={formData.interruptSensitivity ?? "medium"}
                  onChange={(e) => setFormData({ ...formData, interruptSensitivity: e.target.value })}
                >
                  <option value="high">High — triggers easily</option>
                  <option value="medium">Medium — balanced (recommended)</option>
                  <option value="low">Low — requires deliberate input</option>
                </select>
                <p className="text-xs text-slate-400 mt-1">
                  Reduce to "Low" for callers in noisy environments.
                </p>
              </div>
            </div>
          </div>

          {/* Custom vocabulary */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Custom Words & Terms
              <span className="ml-1 text-xs font-normal text-slate-400">(optional)</span>
            </label>
            <textarea
              className="input w-full text-sm"
              placeholder="Comma-separated — e.g., Velaro, HubSpot, Dr. Ramirez"
              rows={2}
              value={formData.vocabularyHints ?? ""}
              onChange={(e) => setFormData({ ...formData, vocabularyHints: e.target.value })}
              maxLength={1024}
            />
            <p className="text-xs text-slate-400 mt-1">
              Unusual names, product names, or industry terms that the transcription engine may mishear.
            </p>
          </div>

          {/* Noise & Environment */}
          <div className="border-t border-slate-100 pt-4">
            <div className="text-sm font-semibold text-slate-700 mb-1">Noise & Environment Handling</div>
            <p className="text-xs text-slate-500 mb-3">
              Controls how the IVR handles background noise, low-quality audio, and noisy caller environments.
            </p>

            {/* Auto noise detection toggle */}
            <label className="flex items-start gap-2 cursor-pointer mb-4">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={formData.autoNoiseDetection ?? false}
                onChange={(e) => setFormData({ ...formData, autoNoiseDetection: e.target.checked })}
              />
              <div>
                <span className="text-sm font-medium text-slate-700">Auto noise detection</span>
                <p className="text-xs text-slate-400">
                  When enabled, the system automatically tightens filtering when it detects a noisy environment
                  (multiple consecutive unclear transcripts). Recommended for sites where callers frequently
                  call from restaurants, events, or outdoors.
                </p>
              </div>
            </label>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Minimum transcript confidence
                </label>
                <select
                  className="input w-full text-sm"
                  value={formData.minConfidenceThreshold ?? 0.5}
                  onChange={(e) => setFormData({ ...formData, minConfidenceThreshold: Number(e.target.value) })}
                >
                  <option value={0.3}>Low (0.3) — accepts most audio, may react to noise</option>
                  <option value={0.4}>Medium-Low (0.4)</option>
                  <option value={0.5}>Standard (0.5) — recommended default</option>
                  <option value={0.6}>Medium-High (0.6) — better for moderate noise</option>
                  <option value={0.7}>High (0.7) — strict, for very noisy environments</option>
                  <option value={0.8}>Very High (0.8) — only very clear speech accepted</option>
                </select>
                <p className="text-xs text-slate-400 mt-1">
                  Speech below this confidence is silently ignored. Higher = fewer false triggers from noise.
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Minimum words to accept
                </label>
                <select
                  className="input w-full text-sm"
                  value={formData.minWordCount ?? 1}
                  onChange={(e) => setFormData({ ...formData, minWordCount: Number(e.target.value) })}
                >
                  <option value={1}>1 word (default)</option>
                  <option value={2}>2 words — filters single-word noise</option>
                  <option value={3}>3 words — strict, may miss short answers</option>
                </select>
                <p className="text-xs text-slate-400 mt-1">
                  Does not affect single-digit voice commands (those always work).
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 mt-3">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Noise advisory after
                </label>
                <select
                  className="input w-full text-sm"
                  value={formData.noiseAdvisoryThreshold ?? 3}
                  onChange={(e) => setFormData({ ...formData, noiseAdvisoryThreshold: Number(e.target.value) })}
                >
                  <option value={0}>Disabled — never send advisory</option>
                  <option value={2}>2 unclear inputs</option>
                  <option value={3}>3 unclear inputs (default)</option>
                  <option value={5}>5 unclear inputs</option>
                </select>
                <p className="text-xs text-slate-400 mt-1">
                  After this many consecutive unclear transcripts, the bot gently lets the caller know.
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Custom advisory message
                  <span className="ml-1 text-xs font-normal text-slate-400">(optional)</span>
                </label>
                <textarea
                  className="input w-full text-sm"
                  placeholder="I'm having a little trouble hearing you clearly. If possible, could you try speaking a bit closer to the phone?"
                  rows={2}
                  maxLength={500}
                  disabled={formData.noiseAdvisoryThreshold === 0}
                  value={formData.noiseAdvisoryMessage ?? ""}
                  onChange={(e) =>
                    setFormData({ ...formData, noiseAdvisoryMessage: e.target.value || undefined })
                  }
                />
              </div>
            </div>
          </div>

          {/* Call Duration */}
          <div className="border-t border-slate-100 pt-4">
            <div className="text-sm font-semibold text-slate-700 mb-1">Call Duration Limit</div>
            <p className="text-xs text-slate-500 mb-3">
              Optional maximum call length. Useful for preventing runaway calls or managing costs.
              Leave blank for the system default (30 minutes).
            </p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Max call duration (minutes)
                </label>
                <input
                  type="number"
                  min={1}
                  max={120}
                  step={1}
                  className="input w-full text-sm"
                  placeholder="30"
                  value={maxDurationMinutes ?? ""}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      maxCallDurationSeconds: e.target.value ? Number(e.target.value) * 60 : undefined
                    })
                  }
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Warn caller with (seconds remaining)
                </label>
                <input
                  type="number"
                  min={10}
                  max={300}
                  step={5}
                  disabled={!formData.maxCallDurationSeconds}
                  className={`input w-full text-sm ${!formData.maxCallDurationSeconds ? "opacity-50 cursor-not-allowed" : ""}`}
                  placeholder="60"
                  value={formData.callDurationWarningSeconds ?? ""}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      callDurationWarningSeconds: e.target.value ? Number(e.target.value) : undefined
                    })
                  }
                />
                <p className="text-xs text-slate-400 mt-1">Plays a warning this many seconds before cut-off.</p>
              </div>
            </div>
            <div className="mt-3">
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Timeout message
                <span className="ml-1 text-xs font-normal text-slate-400">(optional)</span>
              </label>
              <textarea
                className="input w-full text-sm"
                placeholder="Your call has reached the maximum duration. Thank you for contacting us. Goodbye."
                rows={2}
                maxLength={500}
                value={formData.callDurationExceededMessage ?? ""}
                onChange={(e) =>
                  setFormData({ ...formData, callDurationExceededMessage: e.target.value || undefined })
                }
              />
            </div>
          </div>

          {/* Agent Transfer Routing */}
          <div className="border-t border-slate-100 pt-4">
            <div className="text-sm font-semibold text-slate-700 mb-1">Agent Transfer Routing</div>
            <p className="text-xs text-slate-500 mb-3">
              Add department entries to let the bot route transfers by department name (e.g., Sales, Support, Billing).
              If empty, all transfers go to the Bridge Number configured on the IVR integration.
            </p>

            {/* Routing rows */}
            <div className="space-y-2 mb-3">
              {(formData.transferRouting ?? []).map((route, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <input
                    type="text"
                    className="input text-sm flex-1"
                    placeholder="Department name (e.g., Sales)"
                    value={route.label}
                    maxLength={100}
                    onChange={(e) => {
                      const updated = [...(formData.transferRouting ?? [])];
                      updated[idx] = { ...route, label: e.target.value };
                      setFormData({ ...formData, transferRouting: updated });
                    }}
                  />
                  <input
                    type="text"
                    className="input text-sm w-44 font-mono"
                    placeholder="+18005551234"
                    value={route.number}
                    maxLength={20}
                    onChange={(e) => {
                      const updated = [...(formData.transferRouting ?? [])];
                      updated[idx] = { ...route, number: e.target.value };
                      setFormData({ ...formData, transferRouting: updated });
                    }}
                  />
                  <button
                    type="button"
                    className="text-slate-400 hover:text-red-500 transition-colors"
                    aria-label="Remove department"
                    onClick={() => {
                      const updated = (formData.transferRouting ?? []).filter((_, i) => i !== idx);
                      setFormData({ ...formData, transferRouting: updated });
                    }}
                  >
                    <i className="fa fa-trash text-sm" />
                  </button>
                </div>
              ))}
            </div>

            <button
              type="button"
              className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
              onClick={() => {
                const updated = [...(formData.transferRouting ?? []), { label: "", number: "" }];
                setFormData({ ...formData, transferRouting: updated });
              }}
            >
              <i className="fa fa-plus text-xs" />
              Add department
            </button>
          </div>
        </div>

        {/* Transfer Method */}
        <div className="border-t border-slate-200 pt-4">
          <h4 className="font-medium text-slate-700 mb-1">Live Agent Transfer Method</h4>
          <p className="text-xs text-slate-500 mb-3">
            How the call is handed off when the bot transfers to a live agent.
          </p>
          <div className="space-y-3">
            <label className="flex items-start gap-3 cursor-pointer group">
              <input type="radio" name="transferMethod" value="bridge" checked={(formData.transferMethod ?? "bridge") === "bridge"} onChange={() => setFormData({ ...formData, transferMethod: "bridge" })} className="sr-only" />
              <RadioDot checked={(formData.transferMethod ?? "bridge") === "bridge"} className="mt-0.5"
              />
              <div>
                <div className="text-sm font-medium text-slate-700">Bridge Call <span className="text-xs font-normal text-slate-500">(recommended)</span></div>
                <div className="text-xs text-slate-500 mt-0.5">
                  Velaro stays on the call via Twilio Dial. Enables <strong>call recording</strong> and <strong>AI transcription</strong> of the agent leg.
                  Uses Twilio minutes for the agent connection. Best when you need full call logs, agent whispering, or AI wrap-up notes.
                </div>
              </div>
            </label>
            <label className="flex items-start gap-3 cursor-pointer group">
              <input type="radio" name="transferMethod" value="sip_refer" checked={formData.transferMethod === "sip_refer"} onChange={() => setFormData({ ...formData, transferMethod: "sip_refer" })} className="sr-only" />
              <RadioDot checked={formData.transferMethod === "sip_refer"} className="mt-0.5" />
              <div>
                <div className="text-sm font-medium text-slate-700">SIP REFER <span className="text-xs font-normal text-slate-500">(cost-saving)</span></div>
                <div className="text-xs text-slate-500 mt-0.5">
                  Velaro sends a SIP REFER instruction and exits the call. Your PBX or phone system handles the agent leg directly —
                  <strong> no Twilio minutes</strong> and <strong>no Velaro transcription</strong> on the agent leg.
                  Requires a SIP-capable phone system. Best when your PBX already handles recording and you want to minimize cost.
                </div>
                {formData.transferMethod === "sip_refer" && (
                  <div className="mt-2">
                    <label className="block text-xs font-medium text-slate-600 mb-1">SIP URI <span className="font-normal text-slate-400">(optional — leave blank to auto-format from transfer number)</span></label>
                    <input
                      type="text"
                      className="w-full border border-slate-300 rounded px-2 py-1 text-sm"
                      placeholder="e.g. sip:+18005551234@pbx.mycompany.com"
                      value={formData.sipTransferUri ?? ""}
                      onChange={(e) => setFormData({ ...formData, sipTransferUri: e.target.value })}
                    />
                  </div>
                )}
              </div>
            </label>
          </div>
        </div>

        {/* Transfer Behavior */}
        <div className="border-t border-slate-200 pt-4">
          <h4 className="font-medium text-slate-700 mb-1">Transfer Behavior</h4>
          <p className="text-xs text-slate-500 mb-3">
            Controls when and how the bot connects a caller to a live agent.
          </p>

          {/* Always-on transfer number — always visible */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Emergency / Always-On Transfer Number
              <span className="ml-1 text-xs font-normal text-slate-400">(optional)</span>
            </label>
            <input
              type="tel"
              className="input w-full text-sm font-mono"
              placeholder="+18005551234"
              value={formData.alwaysTransferNumber ?? ""}
              onChange={(e) => setFormData({ ...formData, alwaysTransferNumber: e.target.value || undefined })}
              maxLength={20}
            />
            <p className="text-xs text-slate-400 mt-1">
              This number is <strong>ALWAYS called</strong> when the caller requests a live person, regardless of agent availability or transfer mode.
              Use for a guaranteed PSTN trunk (e.g., an answering service or after-hours line).
              Leave blank to rely on the routing table only.
            </p>
          </div>

          {/* Transfer mode select */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Transfer Mode
            </label>
            <div className="space-y-2">
              {TRANSFER_MODE_OPTIONS.map((opt) => {
                const isLocked = opt.gate !== null && !capabilities[opt.gate as keyof IvrCapabilities];
                return (
                  <label
                    key={opt.value}
                    className={`flex items-start gap-3 cursor-pointer group rounded-lg border px-3 py-2 transition-colors ${
                      formData.transferMode === opt.value
                        ? "border-blue-300 bg-blue-50"
                        : "border-slate-200 bg-white hover:border-slate-300"
                    } ${isLocked ? "opacity-60" : ""}`}
                  >
                    <input type="radio" name="transferMode" value={opt.value} checked={(formData.transferMode ?? "always") === opt.value} disabled={isLocked} onChange={() => !isLocked && setFormData({ ...formData, transferMode: opt.value })} className="sr-only" />
                    <RadioDot checked={(formData.transferMode ?? "always") === opt.value} className="mt-0.5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-slate-700">{opt.label}</span>
                        {isLocked && (
                          <span className="text-xs text-slate-400 flex items-center gap-1">
                            🔒 Upgrade required
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5">{opt.description}</div>
                    </div>
                  </label>
                );
              })}
            </div>

            {/* Triage fallback message — only shown when mode = triage */}
            {formData.transferMode === "triage" && (
              <div className="mt-4">
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Triage Menu Message
                  <span className="ml-1 text-xs font-normal text-slate-400">(optional)</span>
                </label>
                <textarea
                  className="input w-full text-sm"
                  rows={3}
                  maxLength={1000}
                  placeholder={DEFAULT_TRIAGE_MESSAGE}
                  value={formData.triageFallbackMessage ?? ""}
                  onChange={(e) =>
                    setFormData({ ...formData, triageFallbackMessage: e.target.value || undefined })
                  }
                />
                <p className="text-xs text-slate-400 mt-1">
                  Played when the bot can't resolve the caller's question. Leave blank to use the default message above.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Idle & Transfer Behavior */}
        <div className="border-t border-slate-200 pt-4">
          <h4 className="font-medium text-slate-700 mb-1">Idle &amp; Transfer Behavior</h4>
          <p className="text-xs text-slate-500 mb-3">
            Controls silence detection timing and what callers hear when a transfer attempt fails.
          </p>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Silence Timeout
                <span className="ml-1 text-xs font-normal text-slate-400">(seconds before "Are you still there?")</span>
              </label>
              <input
                type="number"
                className="input w-32 text-sm"
                min={10}
                max={120}
                placeholder="20"
                value={formData.idleCheckInSeconds ?? ""}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    idleCheckInSeconds: e.target.value ? parseInt(e.target.value) : undefined
                  })
                }
              />
              <p className="text-xs text-slate-400 mt-1">
                How long the bot waits in silence before checking if the caller is still there. Default: 20 seconds.
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Transfer No-Answer Message
                <span className="ml-1 text-xs font-normal text-slate-400">(optional)</span>
              </label>
              <textarea
                className="input w-full text-sm"
                rows={2}
                maxLength={500}
                placeholder="Our agents are unable to take your call right now. Please try calling back shortly. Thank you for calling. Goodbye."
                value={formData.transferNoAnswerMessage ?? ""}
                onChange={(e) =>
                  setFormData({ ...formData, transferNoAnswerMessage: e.target.value || undefined })
                }
              />
              <p className="text-xs text-slate-400 mt-1">
                Played when a live transfer attempt fails (agent's phone rang but wasn't answered). Leave blank to use the default message above.
              </p>
            </div>
            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-slate-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.recordingDisclosureEnabled ?? false}
                  onChange={(e) =>
                    setFormData({ ...formData, recordingDisclosureEnabled: e.target.checked })
                  }
                />
                Play recording disclosure before transfer
                <span className="ml-1 text-xs font-normal text-slate-400">(TCPA compliance)</span>
              </label>
              <p className="text-xs text-slate-400 mt-1 ml-6">
                When enabled, the disclosure message is played to the caller before the bridge transfer begins — before recording starts.
              </p>
              {formData.recordingDisclosureEnabled && (
                <div className="mt-2 ml-6">
                  <textarea
                    className="input w-full text-sm"
                    rows={2}
                    maxLength={500}
                    placeholder="This call may be recorded for quality and training purposes."
                    value={formData.recordingDisclosureText ?? ""}
                    onChange={(e) =>
                      setFormData({ ...formData, recordingDisclosureText: e.target.value || undefined })
                    }
                  />
                  <p className="text-xs text-slate-400 mt-1">
                    Leave blank to use the default: "This call may be recorded for quality and training purposes."
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Escalation Options */}
        <div className="border-t border-slate-200 pt-4">
          <h4 className="font-medium text-slate-700 mb-1">Escalation Options</h4>
          <p className="text-xs text-slate-500 mb-3">
            Controls which escalation paths the bot offers on this IVR line when a caller asks to speak with a person.
            Options are only available if the corresponding subscription feature is also enabled.
          </p>
          <div className="grid grid-cols-2 gap-3">
            <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.escalationBridgeEnabled ?? true}
                onChange={(e) => setFormData({ ...formData, escalationBridgeEnabled: e.target.checked })}
                className="rounded border-slate-300"
              />
              Live phone transfer (bridge call)
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.escalationCallbackEnabled ?? true}
                onChange={(e) => setFormData({ ...formData, escalationCallbackEnabled: e.target.checked })}
                className="rounded border-slate-300"
              />
              Phone callback (immediate or scheduled)
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.escalationSmsEnabled ?? true}
                onChange={(e) => setFormData({ ...formData, escalationSmsEnabled: e.target.checked })}
                className="rounded border-slate-300"
              />
              Text caller a support link or number
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.escalationEmailEnabled ?? true}
                onChange={(e) => setFormData({ ...formData, escalationEmailEnabled: e.target.checked })}
                className="rounded border-slate-300"
              />
              Email caller contact information
            </label>
          </div>
        </div>
      </ModalBody>
      <ModalFooter>
        <div className="flex justify-end gap-2">
          <SecondaryBlueButton label="Cancel" onClick={props.onClose} disabled={sending} />
          <PrimaryBlueButton label={sending ? "Saving…" : "Save"} onClick={handleSubmit} disabled={sending} />
        </div>
      </ModalFooter>
    </Modal>
  );
}
